<template>
    <section class="py-20 sm:py-40 md-max:pb-4 bg-lifeboost-gray md:py-32">
      <div class="max-w-screen-xl px-4 mx-auto space-y-8 md:space-y-24 lg:px-20 sm:px-12">
        <!-- First Section -->
        <div class="grid grid-cols-12 md:grid-cols-10 md:gap-x-12">
          <div class="order-2 col-span-12 mt-8 md:col-span-4 md:order-1 md:mt-0">
            <nuxt-img
              sizes="xs:264px sm:285px md:512px lg:317px xl:284px 2xl:284px"
              :src="lionManeImageSrc"
              :alt="lionManeImageAlt"
              class="object-contain mx-auto md-max:mt-6 md:w-full sm:w-9/12"
              width="466"
              height="299"
              format="webp"
              loading="lazy"
            />
          </div>
  
          <div class="order-1 col-span-12 md:col-span-6 md:order-2">
            <div class="font-bold text-38 sm:text-48 font-ww-medium text-lifeboost-gray-lighter">{{ lionManeTitle }}</div>
            <div
              class="mt-6 sm:mt-12 md:mt-12 text-28 leading-32 sm:leading-38 md:text-26 xl:text-28 text-lifeboost-gray-lighter font-ww-regular"
            >
              {{ lionManeDescription }}
            </div>
  
            <div class="mt-6 space-y-4 sm:space-y-1 lg:mt-8 text-lifeboost-gray-lighter">
              <div
                class="flex px-2 md:px-4 gap-x-4"
                v-for="(feature, index) in lionManeFeatures"
                :key="index"
              >
                <div class="mt-1">
                  <nuxt-img
                    :src="feature.imgSrc"
                    :alt="feature.imgAlt"
                    class=""
                    width="34"
                    height="34"
                    format="webp"
                    loading="lazy"
                  />
                </div>
                <div class="w-full text-lifeboost-gray-lighter leading-32 sm:leading-38 text-28 md:text-26 xl:text-30">
                  <strong class="pr-2 font-ww-medium">{{ feature.title }}</strong>
                  <span class="font-ww-regular">{{ feature.description }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
  
        <!-- Second Section -->
        <div class="grid grid-cols-12 mb-20 md:grid-cols-10 md:gap-x-12 lg:mb-0 md-max:pt-10">
          <div class="order-2 col-span-12 mt-8 mb-10 md:col-span-4 md:order-1 md:mt-0">
            <nuxt-img
              sizes="xs:264px sm:285px md:512px lg:317px xl:284px 2xl:284px"
              :src="chagaImageSrc"
              :alt="chagaImageAlt"
              class="object-contain mx-auto mb-10 md-max:mt-6 md:w-full sm:w-9/12 md:mb-0"
              width="466"
              height="299"
              format="webp"
              loading="lazy"
            />
          </div>
          <div class="order-1 col-span-12 md:col-span-6 md:order-2">
            <div class="font-bold text-38 sm:text-48 font-ww-medium text-lifeboost-gray-lighter">{{ chagaTitle }}</div>
            <div
              class="mt-6 sm:mt-12 md:mt-12 text-28 leading-32 sm:leading-38 md:text-26 xl:text-28 text-lifeboost-gray-lighter font-ww-regular"
            >
              {{ chagaDescription }}
            </div>
  
            <div class="mt-6 space-y-4 sm:space-y-1 lg:mt-8 text-lifeboost-gray-lighter">
              <div
                class="flex px-2 md:px-4 gap-x-4"
                v-for="(feature, index) in chagaFeatures"
                :key="index"
              >
                <div class="mt-1">
                  <nuxt-img
                    :src="feature.imgSrc"
                    :alt="feature.imgAlt"
                    class=""
                    width="34"
                    height="34"
                    format="webp"
                    loading="lazy"
                  />
                </div>
                <div class="w-full text-lifeboost-gray-lighter leading-32 sm:leading-38 text-28 md:text-26 xl:text-30">
                  <strong class="pr-2 font-ww-medium">{{ feature.title }}</strong>
                  <span class="font-ww-regular">{{ feature.description }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </template>
  
  <script setup>
  const props = defineProps({
    lionManeTitle: {
      type: String,
      default: "Lion’s Mane",
    },
    lionManeDescription: {
      type: String,
      default:
        "100% pure mushroom from organic fruiting bodies proven to reduce cognitive impairment and promote nerve and brain health. Contains potent antioxidants, substances that limit the damage of free radicals. Also contains potent anti-inflammatory substances.",
    },
    lionManeImageSrc: {
      type: String,
      default: "/assets/images/mushroom-12.png",
    },
    lionManeImageAlt: {
      type: String,
      default: "Lion's Mane mushroom",
    },
    lionManeFeatures: {
      type: Array,
      default: () => [
        {
          title: "Lab tested",
          description: "to include beta-glucans and to be free of heavy metals, contaminants, and toxins.",
          imgSrc: "/assets/images/check122.png",
          imgAlt: "Lab tested checkbox",
        },
        {
          title: "No starch or myceliated oats",
          description: "(what many low-quality “mushroom” products use).",
          imgSrc: "/assets/images/check122.png",
          imgAlt: "No starch checkbox",
        },
        {
          title: "Triple extracted",
          description: "to contain higher levels of brain-boosting antioxidants and vitamins.",
          imgSrc: "/assets/images/check122.png",
          imgAlt: "Triple extracted checkbox",
        },
      ],
    },
    chagaTitle: {
      type: String,
      default: "Chaga",
    },
    chagaDescription: {
      type: String,
      default:
        "Contains beta-glucans, which boost the immune system. Chage extract can also help prevent the production of harmful cytokines, reducing inflammation in the body. Contains one of the highest ORAC scores (a measure of antioxidant potency). 100% pure mushroom extract (no added fillers, starch, or grains).",
    },
    chagaImageSrc: {
      type: String,
      default: "/assets/images/mushroom-11.png",
    },
    chagaImageAlt: {
      type: String,
      default: "Chaga mushroom",
    },
    chagaFeatures: {
      type: Array,
      default: () => [
        {
          title: "Lab tested",
          description: "to include beta-glucans and to be free of heavy metals, contaminants, and toxins.",
          imgSrc: "/assets/images/check122.png",
          imgAlt: "Lab tested checkbox",
        },
        {
          title: "No starch or myceliated oats",
          description: "(what many low-quality “mushroom” products use).",
          imgSrc: "/assets/images/check122.png",
          imgAlt: "No starch checkbox",
        },
        {
          title: "Triple extracted",
          description: "with an 8:1 extract ratio for maximum potency.",
          imgSrc: "/assets/images/check122.png",
          imgAlt: "Triple extracted checkbox",
        },
      ],
    },
  });
  
  defineBuilderComponent({
    friendlyName: "Content With Left Images",
    inputs: [
      {
        name: "lionManeTitle",
        type: "text",
        friendlyName: "Lion’s Mane Title",
        defaultValue: "Lion’s Mane",
      },
      {
        name: "lionManeDescription",
        type: "richText",
        friendlyName: "Lion’s Mane Description",
        defaultValue:
          "100% pure mushroom from organic fruiting bodies proven to reduce cognitive impairment and promote nerve and brain health. Contains potent antioxidants, substances that limit the damage of free radicals. Also contains potent anti-inflammatory substances.",
      },
      {
        name: "lionManeImageSrc",
        type: "file",
        friendlyName: "Lion’s Mane Image Source",
        allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
        defaultValue: "/assets/images/mushroom-12.png",
      },
      {
        name: "lionManeImageAlt",
        type: "text",
        friendlyName: "Lion’s Mane Image Alt Text",
        defaultValue: "Lion's Mane mushroom",
      },
      {
        name: "lionManeFeatures",
        type: "list",
        friendlyName: "Lion’s Mane Features",
        subFields: [
          {
            name: "title",
            type: "text",
            friendlyName: "Feature Title",
            defaultValue: "",
          },
          {
            name: "description",
            type: "richText",
            friendlyName: "Feature Description",
            defaultValue: "",
          },
          {
            name: "imgSrc",
            type: "file",
            friendlyName: "Feature Image Source",
            allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
            defaultValue: "/assets/images/check122.png",
          },
          {
            name: "imgAlt",
            type: "text",
            friendlyName: "Feature Image Alt Text",
            defaultValue: "Checkbox",
          },
        ],
        defaultValue: [
          {
            title: "Lab tested",
            description: "to include beta-glucans and to be free of heavy metals, contaminants, and toxins.",
            imgSrc: "/assets/images/check122.png",
            imgAlt: "Lab tested checkbox",
          },
          {
            title: "No starch or myceliated oats",
            description: "(what many low-quality “mushroom” products use).",
            imgSrc: "/assets/images/check122.png",
            imgAlt: "No starch checkbox",
          },
          {
            title: "Triple extracted",
            description: "to contain higher levels of brain-boosting antioxidants and vitamins.",
            imgSrc: "/assets/images/check122.png",
            imgAlt: "Triple extracted checkbox",
          },
        ],
      },
      {
        name: "chagaTitle",
        type: "text",
        friendlyName: "Chaga Title",
        defaultValue: "Chaga",
      },
      {
        name: "chagaDescription",
        type: "richText",
        friendlyName: "Chaga Description",
        defaultValue:
          "Contains beta-glucans, which boost the immune system. Chage extract can also help prevent the production of harmful cytokines, reducing inflammation in the body. Contains one of the highest ORAC scores (a measure of antioxidant potency). 100% pure mushroom extract (no added fillers, starch, or grains).",
      },
      {
        name: "chagaImageSrc",
        type: "file",
        friendlyName: "Chaga Image Source",
        allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
        defaultValue: "/assets/images/mushroom-11.png",
      },
      {
        name: "chagaImageAlt",
        type: "text",
        friendlyName: "Chaga Image Alt Text",
        defaultValue: "Chaga mushroom",
      },
      {
        name: "chagaFeatures",
        type: "list",
        friendlyName: "Chaga Features",
        subFields: [
          {
            name: "title",
            type: "text",
            friendlyName: "Feature Title",
            defaultValue: "",
          },
          {
            name: "description",
            type: "richText",
            friendlyName: "Feature Description",
            defaultValue: "",
          },
          {
            name: "imgSrc",
            type: "file",
            friendlyName: "Feature Image Source",
            allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
            defaultValue: "/assets/images/check122.png",
          },
          {
            name: "imgAlt",
            type: "text",
            friendlyName: "Feature Image Alt Text",
            defaultValue: "Checkbox",
          },
        ],
        defaultValue: [
          {
            title: "Lab tested",
            description: "to include beta-glucans and to be free of heavy metals, contaminants, and toxins.",
            imgSrc: "/assets/images/check122.png",
            imgAlt: "Lab tested checkbox",
          },
          {
            title: "No starch or myceliated oats",
            description: "(what many low-quality “mushroom” products use).",
            imgSrc: "/assets/images/check122.png",
            imgAlt: "No starch checkbox",
          },
          {
            title: "Triple extracted",
            description: "with an 8:1 extract ratio for maximum potency.",
            imgSrc: "/assets/images/check122.png",
            imgAlt: "Triple extracted checkbox",
          },
        ],
      },
    ],
  });
  </script>
  